'use client'
import React, { useContext, useEffect, useState } from 'react';
import { forwardRef } from '@chakra-ui/system';
import { Select } from 'chakra-react-select';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Button,
  Skeleton,
} from '@chakra-ui/react';
import { Box, Text } from '@chakra-ui/layout';
import { Controller, useForm } from 'react-hook-form';
import { useSharedExperiencesApi } from '../../../shared-experiences/lib/useSharedExperiencesApi';
import { AuthContext } from '../../../auth/lib/authContext';

const AddSharedExperienceToExperienceForm = forwardRef((props, ref) => {
  const {
    hideSave,
    onSubmitted,
    onSubmitting,
    experienceId,
    onCreateNew
  } = props;
  const {
    handleSubmit,
    errors,
    control,
    formState: { isSubmitting, isValid }
  } = useForm();
  const sharedExperiencesApi = useSharedExperiencesApi();
  const { profile } = useContext(AuthContext);
  const [sharedExperienceOptionsList, setSharedExperienceOptionsList] = useState(null);

  const onSubmit = async (data) => {
    try {
      if (onSubmitting) {
        onSubmitting();
      }
      const sharedExperienceId = data.sharedExperience.value;
      await sharedExperiencesApi.addExperience(sharedExperienceId, experienceId);
      if (onSubmitted) {
        onSubmitted();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchData = async () => {
    try {
      const availableSharedExperiences = await sharedExperiencesApi.fetchSharedExperiencesNotContainingExperience(profile.id, experienceId);
      const newSharedExperienceList = availableSharedExperiences.map((se) => {
        return { label: se.name, value: se.id }
      });

      if (onCreateNew) {
        newSharedExperienceList.unshift({
          label: '+ Create New Shared Experience',
          value: -1,
        });
      }
      setSharedExperienceOptionsList(newSharedExperienceList);
    } catch (e) {
      console.error(e)
    }
  };

  useEffect(() => {
    if (experienceId && profile) {
      fetchData();
    }
  }, [experienceId, profile]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Controller
          control={control}
          name="sharedExperience"
          rules={{ required: "Please select a shared experience" }}
          render={({
            field: { onChange, onBlur, value, name, ref },
            fieldState: { invalid, error }
          }) => (
            <FormControl isInvalid={errors && errors.sharedExperience}>
              <FormLabel>Select a shared experience</FormLabel>
              {!sharedExperienceOptionsList && (
                <Skeleton w={"100%"} height={"30px"} />
              )}
              {sharedExperienceOptionsList && sharedExperienceOptionsList.length > 0 && (
                <Select
                  options={sharedExperienceOptionsList}
                  onChange={(e) => {
                    if (e.value === -1 && onCreateNew) {
                      onCreateNew();
                    } else {
                      onChange(e);
                    }
                  }}
                  onBlur={onBlur}
                  value={value}
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: base => ({ ...base, zIndex: 9999 }) }}
                  formatOptionLabel={(option) => (
                    <div>
                      <div style={{ fontWeight: 'medium' }}>{option.label}</div>
                    </div>
                  )}
                />
              )}
              {sharedExperienceOptionsList && sharedExperienceOptionsList.length === 0 && (
                <Box p={3} textAlign="center">
                  <Text color="text.light.grey" fontSize="12px">
                    No shared experiences available.{' '}
                    {onCreateNew && (
                      <Button
                        variant="link"
                        size="sm"
                        color="fill.coral"
                        onClick={onCreateNew}
                        p={0}
                        h="auto"
                        minW="auto"
                        fontSize="12px"
                      >
                        Create a new one?
                      </Button>
                    )}
                  </Text>
                </Box>
              )}
              <FormErrorMessage>{errors && errors.sharedExperience && errors.sharedExperience.message}</FormErrorMessage>
            </FormControl>
          )}
        />
        <Button
          style={{
            display: hideSave ? 'none' : 'inherit',
          }}
          ref={ref}
          isLoading={isSubmitting}
          disabled={!isValid}
          type='submit'
          my={2}
        >Save</Button>
      </form>
    </>
  );
});

AddSharedExperienceToExperienceForm.displayName = 'AddSharedExperienceToExperienceForm';

export default AddSharedExperienceToExperienceForm; 